import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import * as Yup from "yup";
import includes from "lodash/includes";
import findIndex from "lodash/findIndex";
import map from "lodash/map";
import some from "lodash/some";
import * as ProgramApi from "api/ProgramApi";
import * as ProgramActions from "actions/ProgramActions";
import Button from "components/common/Button";
import CheckboxInput from "components/common/CheckboxInput";
import SignatureBox from "components/common/SignatureBox";
import SpinnerTakeover from "components/common/SpinnerTakeover";
import { handleErrorResponse } from "utils/request";
import useFormHandlers from "utils/useFormHandlers";
import { DANGER, SUCCESS } from "constants/ButtonTypes";
import { CANCELED, DECLINE, DECLINED, ELIGIBLE, ENROLL, ENROLLED } from "constants/ProgramStatuses";

const CHECKBOX = "Checkbox";

const schema = Yup.object().shape({
	acceptedTerms: Yup.bool().oneOf([true], 'Field must be checked').default(false).required(),
	formFieldsJson: Yup.array().of(
		Yup.object().shape({
			ProgramServiceName: Yup.string(),
			DoEnroll: Yup.bool().default(false),
		})
	).default([]),
	signature: Yup.string().required(),
	status: Yup.string(),
});

function ProgramForm(props) {
	const {
		clinicProgramServiceStatusUpdated,
		clinicProgramStatusUpdated,
		onAfterSave,
		onCancel,
		onClose,
		parentProgram,
		program,
	} = props;
	const { formData, handleUpdate, isValid, invalidFields } = useFormHandlers(
		{
			...program,
			signature: "",
			acceptedTerms: false,
		},
		schema
	);
	const [loading, setLoading] = useState(false);

	const validationCheck = () => {
		const programList = [4];

		if(!parentProgram && programList.includes(program.programId)) {
			const canSubmit = isValid && some(formData.formFieldsJson, field => field.DoEnroll === true);
			return !canSubmit;			
		} else {
			return !isValid;
		}
	}

	const isDisabled = validationCheck();

	const handleUpdateStatus = async (updatedProgram) => {
		setLoading(true);
		if (!!parentProgram) {
			const programService = {
				...updatedProgram,
				programId: parentProgram,
				formFieldsJson: JSON.stringify(updatedProgram.formFieldsJson)
			};
			await ProgramApi.updateClinicProgramServiceStatus(programService)
		        .then((res) => {
		            clinicProgramServiceStatusUpdated(programService);
					if (onAfterSave) {
						onAfterSave();
					}
		        })
		        .catch((error) => {
					handleErrorResponse("updating clinic program service", error);
		        })
				.finally(() => {
					setLoading(false);
				});
		} else {
			const program = {
				...updatedProgram,
				formFieldsJson: JSON.stringify(updatedProgram.formFieldsJson)
			};
			await ProgramApi.updateClinicProgramStatus(program)
	            .then((res) => {
	                clinicProgramStatusUpdated(program);
					if (onAfterSave) {
						onAfterSave();
					}
	            })
	            .catch((error) => {
					handleErrorResponse("updating clinic program", error);
	            })
				.finally(() => {
					setLoading(false);
				});
		}
	}

	const handleDecline = () => {
		const updatedProgram = {
			...formData,
			status: DECLINE,
		};
		handleUpdateStatus(updatedProgram);
		onClose();
	};

	const handleSubmit = (e) => {
		e.stopPropagation();
		e.preventDefault();

		const updatedProgram = {
			...formData,
			status: ENROLL,
		};
		handleUpdateStatus(updatedProgram);
		onClose();
	};

	const handleCancelClicked = () => {
		onCancel();
	}

	const handleIframeLoaded = () => {
		const iFrameID = document.getElementById(`program_iframe_${program.id}`);
        if (iFrameID) {
            // here you can make the height, I delete it first, then I make it again
            iFrameID.height = "";
            iFrameID.height = iFrameID.contentWindow.document.body.scrollHeight + 40 + "px";
        }
	};

	const canEnroll = (program.status === ELIGIBLE || program.status === CANCELED || program.status === DECLINED);

	return (
		<form onSubmit={handleSubmit}>
			{/*<div dangerouslySetInnerHTML={{__html: program.body}} />*/}
			<iframe
				id={`program_iframe_${program.id}`}
				style={{
					width: "100%",
					flex: "none",
					border: "none",

				}}
				scrolling="no"
		        srcDoc={program?.body}
				onLoad={handleIframeLoaded}
			/>
			<div>
				{map(formData.optInFormConfigJson, (config, index) => {
					const formFieldIndex = findIndex(formData.formFieldsJson, { ProgramServiceName: config.ProgramServiceName });
					const formField = formData.formFieldsJson[formFieldIndex];
						switch (config.Type) {
							case CHECKBOX:
								return (
									<CheckboxInput
										key={index}
										checked={formField.DoEnroll}
										name={`formFieldsJson[${formFieldIndex}].DoEnroll`}
										onChange={handleUpdate}
										label={config.DisplayText}
										disabled={!canEnroll}
									/>
								);
							default:
								return <div key={index}>TODO: Type not yet supported: {config.Type}</div>;
						}
				})}
			</div>
			{program.status !== ELIGIBLE && (
				<div className="text-center">
					{program.status} on {moment(program.userActionDate).format("MM/DD/YYYY")} by {program.signature}
				</div>
			)}
			{canEnroll && (
				<div className="margin-top-md">
					<SignatureBox
						onChange={handleUpdate}
						name="signature"
						value={formData.signature}
						hasError={!!includes(invalidFields, "signature")}
						label="Please Sign Here"
						autoFocus
					/>
					<CheckboxInput
						name="acceptedTerms"
						onChange={handleUpdate}
						hasError={!!includes(invalidFields, "acceptedTerms")}
						label="I accept the terms and conditions"
						checked={formData.acceptedTerms}
					/>
				</div>
			)}
			<div className="flex spaced-content border-top align-center">
				{((program.status === ENROLLED) && onCancel) && (
					<>
						<div className="flex-1">
							<Button
								type={DANGER}
								onClick={handleCancelClicked}
								text
							>
								Cancel Enrollment
							</Button>
						</div>
					</>
				)}
				{program.status === CANCELED && (
					<div className="flex-1"/>
				)}
				{program.status === ELIGIBLE && (
					<Button
						type={DANGER}
						onClick={handleDecline}
						disabled={!formData.signature}
					>
						Decline
					</Button>
				)}
				{canEnroll && (
					<Button
						buttonType="submit"
						type={SUCCESS}
						disabled={!formData.signature || !formData.acceptedTerms || isDisabled}
					>
						Enroll
					</Button>
				)}
			</div>
			<SpinnerTakeover show={loading} />
		</form>
	);
}

ProgramForm.propTypes = {
	clinicProgramServiceStatusUpdated: PropTypes.func,
	clinicProgramStatusUpdated: PropTypes.func,
	onAfterSave: PropTypes.func,
	onCancel: PropTypes.func,
	onClose: PropTypes.func.isRequired,
	parentProgram: PropTypes.number,
	program: PropTypes.object.isRequired,
};


export default connect(
	(state, ownProps) => ({
		programServices: state.entities.clinicProgramServices?.[ownProps.program.clinicId]?.[ownProps.program.programId] || {},
	}),
	(dispatch) => ({
		clinicProgramStatusUpdated: (program) => dispatch(ProgramActions.clinicProgramStatusUpdated(program)),
		clinicProgramServiceStatusUpdated: (programService) => dispatch(ProgramActions.clinicProgramServiceStatusUpdated(programService)),
	})
)(ProgramForm);
